import React from "react";
import { motion } from "framer-motion";
import { Sparkles, Heart, Compass, MapPin, Phone, Mail, Star, BookOpen } from "lucide-react";
import { ASSETS, CONTACT_INFO } from "@/data/site";

const scrollTo = (id) => () => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

const sections = [
  {
    icon: Sparkles,
    title: "More Than Just Reading Cards",
    body: "To me, a card reading is so much more than looking up meanings in a book. The cards are a bridge — connecting me with you, and with the Infinite Wisdom of the Universe. I use them to tune into your energy and interpret the wisdom that is there for you to receive. What never ceases to amaze me is how profoundly accurate the messages are, and how precisely they speak to exactly what is unfolding in your life at that moment.",
  },
  {
    icon: Heart,
    title: "What a Reading Can Do For You",
    body: "A card reading can bring clarity, reassurance, and a fresh sense of direction — whether you are at a crossroads, seeking answers, or simply ready to connect with something deeper. Clients often leave feeling lighter, clearer, and quietly inspired.",
  },
  {
    icon: Compass,
    title: "Come and Find Me",
    body: "If you live in or are visiting beautiful Whitianga, I invite you to come around for a personal reading in a relaxed and welcoming space. There is something special about sitting together in person — the energy flows freely, and the experience tends to be deeply moving. Or come and find me at the local Whitianga markets held on a Saturday morning over spring and summer. If you are outside of Whitianga, no worries — readings are also available by phone or online.",
  },
];

const AboutSection = () => {
  return (
    <section id="about" className="relative section tone-about starfield-dense" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <p className="text-xs tracking-[0.3em] uppercase mb-3" style={{ color: "var(--cs-cyan)" }}>About</p>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="font-serif-display text-5xl md:text-6xl leading-tight text-white"
            data-testid="about-heading"
          >
            Welcome — I'm <span className="italic" style={{ color: "var(--cs-rose)" }}>Verna</span>
          </motion.h2>
        </div>

        <div className="grid lg:grid-cols-5 gap-10 lg:gap-14 items-start">
          {/* LEFT image */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-2 relative lg:sticky lg:top-28"
          >
            <div className="absolute -inset-5 rounded-3xl blur-3xl opacity-40"
                 style={{ background: "radial-gradient(circle, rgba(210,146,140,0.5), transparent 70%)" }} />
            <div className="relative rounded-3xl overflow-hidden border border-white/15">
              <img
                src={ASSETS.vernaMarket}
                alt="Verna at the Whitianga market"
                className="w-full h-auto object-cover"
                data-testid="about-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0F1437]/70 via-transparent to-transparent pointer-events-none" />
            </div>
            <div className="mt-5 glass rounded-2xl p-5">
              <div className="flex items-start gap-3 mb-3">
                <MapPin className="w-4 h-4 mt-1" style={{ color: "var(--cs-cyan)" }} />
                <p className="text-slate-100 text-sm">Whitianga, New Zealand — and online wherever you are.</p>
              </div>
              <div className="flex flex-wrap items-center gap-4 pt-3 border-t border-white/5">
                <a href={CONTACT_INFO.phoneHref} className="flex items-center gap-2 text-sm text-slate-100 hover:text-cyan-accent" style={{ color: "#E2E8F0" }} data-testid="about-phone">
                  <Phone className="w-3.5 h-3.5" /> {CONTACT_INFO.phone}
                </a>
                <a href={CONTACT_INFO.emailHref} className="flex items-center gap-2 text-sm text-slate-100" style={{ color: "#E2E8F0" }} data-testid="about-email">
                  <Mail className="w-3.5 h-3.5" /> Email
                </a>
              </div>
            </div>
          </motion.div>

          {/* RIGHT bio */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="lg:col-span-3 space-y-7"
          >
            <div className="glass rounded-3xl p-8 md:p-10">
              <p className="text-slate-100 text-lg leading-relaxed">
                With <span className="font-medium" style={{ color: "var(--cs-rose)" }}>35 years</span> of experience working with the cards, I bring a depth of insight, intuition, and heartfelt guidance to every reading. My journey with the cards has been a deeply personal and spiritual one — shaped not only by years of practice, but by the loving and gentle guidance of high vibrational spiritual beings who have offered me valuable tools for learning, growth, and personal and spiritual development.
              </p>
              <p className="mt-5 text-slate-100 text-lg leading-relaxed">
                Having worked as a professional reader for one of New Zealand's most respected tarot practices, I now offer deeply personal, one-on-one readings — offering the same professional standard to you at <span className="font-medium" style={{ color: "var(--cs-rose)" }}>half their price</span>.
              </p>
            </div>

            {sections.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.1 }}
                  className="glass rounded-3xl p-8 md:p-10"
                  data-testid={`about-section-${i}`}
                >
                  <div className="flex items-center gap-4 mb-4">
                    <span className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
                          style={{ background: "linear-gradient(135deg, var(--cs-rose), var(--cs-purple))" }}>
                      <Icon className="w-5 h-5 text-white" strokeWidth={2.2} />
                    </span>
                    <h3 className="font-serif-display text-2xl md:text-3xl" style={{ color: "var(--cs-rose)" }}>{s.title}</h3>
                  </div>
                  <p className="text-slate-100 leading-relaxed">{s.body}</p>
                </motion.div>
              );
            })}

            <ul className="space-y-3 pl-2">
              {[
                "35+ years of professional tarot practice",
                "In-person, phone, and online readings",
                "Whitianga markets every Saturday — spring & summer",
                "Personal, heartfelt, and grounded guidance",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-100">
                  <Star className="w-4 h-4 mt-1 shrink-0 fill-current" style={{ color: "var(--cs-cyan)" }} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            <div className="flex flex-wrap gap-4 pt-2">
              <button onClick={scrollTo("contact")} className="btn-pill-primary" data-testid="about-cta-book">
                <Sparkles className="w-4 h-4" /> Book a reading with Verna
              </button>
              <button onClick={scrollTo("tarot")} className="btn-pill-blue" data-testid="about-cta-tarot">
                <BookOpen className="w-4 h-4" /> Explore the tarot
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
